import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import EmailDeliveryCard from '../components/admin/EmailDeliveryCard';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Bell, UserCog, Shield, Search, Play, Square, Clock, 
  Mail, AlertTriangle, CheckCircle, Eye, EyeOff, RefreshCw,
  FileText, Users, Activity, Settings, Wifi, WifiOff, Trash2,
  Rss, Newspaper, Sparkles, Info
} from 'lucide-react';
import { useWebSocket } from '../hooks/useWebSocket';
import FeatureFlagsPanel from '../components/FeatureFlagsPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Notification categories for grouping
const notificationCategories = {
  Security: ['suspicious_voting', 'user_reported', 'content_flagged', 'negative_reputation_activity'],
  Content: ['submission_promoted', 'viral_threshold_reached', 'new_registrations_digest', 'low_engagement_alerts'],
  System: ['api_errors', 'youtube_refresh_failures', 'scheduled_task_failures']
};

export default function SysadminDashboard() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('notifications');
  
  // Notification state
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [notificationSettings, setNotificationSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [realtimeAlerts, setRealtimeAlerts] = useState([]);
  
  // Impersonation state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionHistory, setSessionHistory] = useState([]);
  const [impersonateDialogOpen, setImpersonateDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [impersonateForm, setImpersonateForm] = useState({
    reason: '',
    ticket_number: '',
    mode: 'read_only'
  });
  const [startingSession, setStartingSession] = useState(false);
  
  // Audit log state
  const [selectedSessionForAudit, setSelectedSessionForAudit] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);

  // Deletion queue state
  const [deletionQueue, setDeletionQueue] = useState({ requests: [], summary: {} });
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [processingDeletion, setProcessingDeletion] = useState(false);

  // Real-time notifications via WebSocket
  const handleRealtimeNotification = useCallback((data) => {
    
    
    // Add to alerts list
    setRealtimeAlerts(prev => [{
      id: Date.now(),
      ...data,
      receivedAt: new Date().toISOString()
    }, ...prev.slice(0, 19)]); // Keep last 20
    
    // Show toast based on priority
    if (data.priority === 'critical' || data.priority === 'high') {
      toast.warning(
        <div>
          <p className="font-semibold">{data.category === 'suspicious_activity' ? '🚨 Security Alert' : '⚠️ Alert'}</p>
          <p className="text-sm">{data.details?.message || 'New alert'}</p>
        </div>,
        { duration: 8000 }
      );
    } else if (data.category === 'content') {
      toast.success(
        <div>
          <p className="font-semibold">Content Update</p>
          <p className="text-sm">{data.details?.message || data.details?.title}</p>
        </div>,
        { duration: 5000 }
      );
    }
  }, []);

  const { isConnected, connectionError } = useWebSocket(handleRealtimeNotification);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading, navigate]);

  const fetchData = async () => {
    setLoading(true);
    const init = { credentials: 'include' };
    
    try {
      // Fetch notification status and settings
      const [statusRes, settingsRes, activeRes, historyRes, deletionRes] = await Promise.all([
        fetch(`${API_URL}/api/notifications/status`, init),
        fetch(`${API_URL}/api/notifications/settings`, init),
        fetch(`${API_URL}/api/support/impersonate/active`, init),
        fetch(`${API_URL}/api/support/impersonate/history?limit=20`, init),
        fetch(`${API_URL}/api/privacy/admin/deletion-queue`, init)
      ]);
      
      if (statusRes.ok) setNotificationStatus(await statusRes.json());
      if (settingsRes.ok) setNotificationSettings(await settingsRes.json());
      if (activeRes.ok) {
        const data = await activeRes.json();
        setActiveSession(data.active_session);
      }
      if (historyRes.ok) setSessionHistory(await historyRes.json());
      if (deletionRes.ok) setDeletionQueue(await deletionRes.json());
      
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleNotification = (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        [key]: value
      }
    }));
  };

  const handleSaveNotificationSettings = async () => {
    setSavingSettings(true);
    try {
      const res = await fetch(`${API_URL}/api/notifications/settings`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notifications_enabled: notificationSettings.notifications_enabled,
          preferences: notificationSettings.preferences,
          instant_notifications: notificationSettings.instant_notifications,
          daily_digest: notificationSettings.daily_digest,
          digest_time_utc: notificationSettings.digest_time_utc
        })
      });
      
      if (res.ok) {
        toast.success('Notification settings saved');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Error saving settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSearchUsers = async () => {
    if (!searchQuery || searchQuery.length < 2) return;
    
    setSearching(true);
    try {
      const res = await fetch(
        `${API_URL}/api/support/users/search?query=${encodeURIComponent(searchQuery)}`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        setSearchResults(await res.json());
      } else {
        toast.error('Search failed');
      }
    } catch (err) {
      toast.error('Error searching users');
    } finally {
      setSearching(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!selectedUser || !impersonateForm.reason) {
      toast.error('Please provide a reason for impersonation');
      return;
    }
    
    if (impersonateForm.mode === 'full_access' && !impersonateForm.ticket_number) {
      toast.error('Full access mode requires a ticket number');
      return;
    }
    
    setStartingSession(true);
    try {
      const res = await fetch(`${API_URL}/api/support/impersonate`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          target_user_id: selectedUser.id,
          reason: impersonateForm.reason,
          ticket_number: impersonateForm.ticket_number || null,
          mode: impersonateForm.mode
        })
      });
      
      if (res.ok) {
        const session = await res.json();
        setActiveSession(session);
        setImpersonateDialogOpen(false);
        toast.success(`Now viewing as ${selectedUser.username}`);
        
        // Store impersonation token for use
        localStorage.setItem('impersonation_token', session.session_token);
        localStorage.setItem('impersonation_session_id', session.id);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to start impersonation');
      }
    } catch (err) {
      toast.error('Error starting impersonation');
    } finally {
      setStartingSession(false);
    }
  };

  const handleEndImpersonation = async () => {
    const sessionId = activeSession?.id || localStorage.getItem('impersonation_session_id');
    if (!sessionId) return;
    
    try {
      const res = await fetch(`${API_URL}/api/support/impersonate/${sessionId}/end`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (res.ok) {
        setActiveSession(null);
        localStorage.removeItem('impersonation_token');
        localStorage.removeItem('impersonation_session_id');
        toast.success('Impersonation session ended');
        fetchData(); // Refresh history
      } else {
        toast.error('Failed to end session');
      }
    } catch (err) {
      toast.error('Error ending session');
    }
  };

  const handleViewAuditLog = async (sessionId) => {
    setSelectedSessionForAudit(sessionId);
    try {
      const res = await fetch(
        `${API_URL}/api/support/impersonate/${sessionId}/audit-log`,
        { credentials: 'include' }
      );
      
      if (res.ok) {
        const data = await res.json();
        setAuditLogs(data.logs);
        setAuditDialogOpen(true);
      }
    } catch (err) {
      toast.error('Error loading audit log');
    }
  };

  const handleSendTestEmail = async () => {
    try {
      const res = await fetch(`${API_URL}/api/notifications/test`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (res.ok) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send test email');
      }
    } catch (err) {
      toast.error('Error sending test email');
    }
  };

  const fetchDeletionQueue = async () => {
    setDeletionLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/admin/deletion-queue`, {
        credentials: 'include',
      });
      if (res.ok) setDeletionQueue(await res.json());
    } catch (err) {
      console.error('Error fetching deletion queue:', err);
    } finally {
      setDeletionLoading(false);
    }
  };

  const handleProcessDeletions = async () => {
    setProcessingDeletion(true);
    try {
      const res = await fetch(`${API_URL}/api/privacy/admin/deletion-queue/process`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        toast.success('Deletion processing triggered');
        await fetchDeletionQueue();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to process deletions');
      }
    } catch (err) {
      toast.error('Error triggering deletion processing');
    } finally {
      setProcessingDeletion(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Sysadmin Dashboard</h1>
              <p className="text-gray-600">Platform administration and support tools</p>
            </div>
          </div>
          {/* WebSocket Connection Status */}
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border shadow-sm">
            {isConnected ? (
              <>
                <Wifi className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-700">Live Updates</span>
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 text-gray-400" />
                <span className="text-sm text-gray-500">Connecting...</span>
              </>
            )}
          </div>
        </div>

        {/* Real-time Alerts Banner */}
        {realtimeAlerts.length > 0 && realtimeAlerts[0].category === 'suspicious_activity' && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="font-medium text-red-900">
                  Latest Alert: {realtimeAlerts[0].details?.message || 'Suspicious activity detected'}
                </span>
                <span className="text-xs text-red-600">
                  {new Date(realtimeAlerts[0].receivedAt).toLocaleTimeString()}
                </span>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setRealtimeAlerts([])}
                className="text-red-600 hover:text-red-800"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Active Impersonation Banner */}
        {activeSession && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-medium text-amber-900">
                  Active Impersonation: Viewing as <strong>{activeSession.target_username}</strong>
                </p>
                <p className="text-sm text-amber-700">
                  Mode: {activeSession.mode} | Started: {new Date(activeSession.started_at).toLocaleTimeString()}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={handleEndImpersonation} className="border-amber-300">
              <Square className="h-4 w-4 mr-2" /> End Session
            </Button>
          </div>
        )}

        {/* Quick Admin Links */}
        <div className="mb-6 flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/blog'}>
            <FileText className="h-4 w-4 mr-2" /> Blog Admin
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/setup'}>
            <Settings className="h-4 w-4 mr-2" /> DB Setup
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/feed-sources'}>
            <Rss className="h-4 w-4 mr-2" /> Feed Sources
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/admin/news-sources'}>
            <Newspaper className="h-4 w-4 mr-2" /> News Sources
          </Button>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="notifications" data-testid="tab-notifications">
              <Bell className="h-4 w-4 mr-2" /> Notifications
            </TabsTrigger>
            <TabsTrigger value="realtime" data-testid="tab-realtime">
              <Activity className="h-4 w-4 mr-2" /> Live Feed
              {realtimeAlerts.length > 0 && (
                <Badge className="ml-2 bg-red-500">{realtimeAlerts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="impersonation" data-testid="tab-impersonation">
              <UserCog className="h-4 w-4 mr-2" /> Impersonation
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <FileText className="h-4 w-4 mr-2" /> Audit History
            </TabsTrigger>
            <TabsTrigger value="deletions" data-testid="tab-deletions" onClick={fetchDeletionQueue}>
              <Trash2 className="h-4 w-4 mr-2" /> Deletion Queue
              {deletionQueue.summary?.pending > 0 && (
                <Badge className="ml-2 bg-amber-500">{deletionQueue.summary.pending}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="feature-flags" data-testid="tab-feature-flags">
              <Sparkles className="h-4 w-4 mr-2" /> Feature Flags
            </TabsTrigger>
          </TabsList>

          {/* FEATURE FLAGS TAB */}
          <TabsContent value="feature-flags">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-amber-500" /> AI Feature Unlocks
                </CardTitle>
                <CardDescription>
                  Toggle AI features on once thresholds are reached. Visible on the Community Pulse bar and page.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FeatureFlagsPanel />
              </CardContent>
            </Card>
          </TabsContent>

          {/* NOTIFICATIONS TAB */}
          <TabsContent value="notifications">
            <EmailDeliveryCard />
            <div className="grid gap-6 md:grid-cols-2 mt-6">
              {/* Master Settings */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="h-5 w-5" /> Delivery Settings
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Enable Notifications</Label>
                      <p className="text-xs text-gray-500">Master toggle for all notifications</p>
                    </div>
                    <Switch
                      checked={notificationSettings?.notifications_enabled}
                      onCheckedChange={(val) => setNotificationSettings(prev => ({...prev, notifications_enabled: val}))}
                      data-testid="toggle-notifications-enabled"
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Instant Notifications</Label>
                      <p className="text-xs text-gray-500">Send emails immediately</p>
                    </div>
                    <Switch
                      checked={notificationSettings?.instant_notifications}
                      onCheckedChange={(val) => setNotificationSettings(prev => ({...prev, instant_notifications: val}))}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Daily Digest</Label>
                      <p className="text-xs text-gray-500">Consolidate into one daily email</p>
                    </div>
                    <Switch
                      checked={notificationSettings?.daily_digest}
                      onCheckedChange={(val) => setNotificationSettings(prev => ({...prev, daily_digest: val}))}
                    />
                  </div>
                  
                  {notificationSettings?.daily_digest && (
                    <div>
                      <Label>Digest Time (UTC)</Label>
                      <Select
                        value={String(notificationSettings?.digest_time_utc || 8)}
                        onValueChange={(val) => setNotificationSettings(prev => ({...prev, digest_time_utc: parseInt(val)}))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({length: 24}, (_, i) => (
                            <SelectItem key={i} value={String(i)}>{i}:00 UTC</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  
                  <div className="pt-4 flex gap-2">
                    <Button onClick={handleSaveNotificationSettings} disabled={savingSettings} className="flex-1">
                      {savingSettings ? 'Saving...' : 'Save Settings'}
                    </Button>
                    <Button variant="outline" onClick={handleSendTestEmail} disabled={!notificationStatus?.email_configured}>
                      <Mail className="h-4 w-4 mr-2" /> Test
                    </Button>
                  </div>
                  
                  {!notificationStatus?.email_configured && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> Email not configured - see PRD.md for setup
                    </p>
                  )}
                </CardContent>
              </Card>

              {/* Notification Types */}
              <Card>
                <CardHeader>
                  <CardTitle>Notification Types</CardTitle>
                  <CardDescription>Choose which notifications to receive</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {Object.entries(notificationCategories).map(([category, keys]) => (
                    <div key={category}>
                      <h4 className="font-semibold text-sm text-gray-700 mb-2">{category}</h4>
                      <div className="space-y-2">
                        {keys.map(key => {
                          const typeInfo = notificationStatus?.notification_types?.find(t => t.key === key);
                          return (
                            <div key={key} className="flex items-center justify-between py-1">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm">{typeInfo?.label || key}</span>
                                {typeInfo?.frequency && (
                                  <TooltipProvider delayDuration={150}>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <button
                                          type="button"
                                          className="text-gray-400 hover:text-gray-600 focus:outline-none"
                                          aria-label={`Frequency info for ${typeInfo.label}`}
                                          data-testid={`freq-info-${key}`}
                                        >
                                          <Info className="h-3.5 w-3.5" />
                                        </button>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-xs">
                                        {typeInfo.frequency}
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                              <Switch
                                checked={notificationSettings?.preferences?.[key] ?? false}
                                onCheckedChange={(val) => handleToggleNotification(key, val)}
                                data-testid={`toggle-${key}`}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* LIVE FEED TAB */}
          <TabsContent value="realtime">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5" /> Real-Time Activity Feed
                </CardTitle>
                <CardDescription>
                  Live notifications from the platform - suspicious activity, content updates, and system events
                </CardDescription>
              </CardHeader>
              <CardContent>
                {realtimeAlerts.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-2">No alerts yet</p>
                    <p className="text-gray-400 text-sm">
                      Real-time notifications will appear here when events occur
                    </p>
                    <div className="mt-4 flex items-center justify-center gap-2">
                      {isConnected ? (
                        <>
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                          <span className="text-green-600 text-sm">Connected and listening...</span>
                        </>
                      ) : (
                        <>
                          <span className="w-2 h-2 bg-gray-400 rounded-full" />
                          <span className="text-gray-500 text-sm">Connecting...</span>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {realtimeAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border ${
                          alert.priority === 'critical' || alert.priority === 'high'
                            ? 'bg-red-50 border-red-200'
                            : alert.category === 'content'
                            ? 'bg-green-50 border-green-200'
                            : 'bg-blue-50 border-blue-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            {alert.category === 'suspicious_activity' ? (
                              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
                            ) : alert.category === 'content' ? (
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                            ) : (
                              <Bell className="h-5 w-5 text-blue-600 mt-0.5" />
                            )}
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">
                                  {alert.activity_type || alert.event || alert.category}
                                </span>
                                <Badge
                                  variant={
                                    alert.priority === 'critical'
                                      ? 'destructive'
                                      : alert.priority === 'high'
                                      ? 'destructive'
                                      : 'secondary'
                                  }
                                  className="text-xs"
                                >
                                  {alert.priority}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                {alert.details?.message || alert.details?.reason || JSON.stringify(alert.details)}
                              </p>
                            </div>
                          </div>
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {new Date(alert.receivedAt).toLocaleTimeString()}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {realtimeAlerts.length > 0 && (
                  <div className="mt-4 pt-4 border-t flex justify-between items-center">
                    <span className="text-sm text-gray-500">
                      {realtimeAlerts.length} alert{realtimeAlerts.length !== 1 ? 's' : ''} received
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setRealtimeAlerts([])}
                    >
                      Clear All
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* IMPERSONATION TAB */}
          <TabsContent value="impersonation">
            <div className="grid gap-6 md:grid-cols-2">
              {/* User Search */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Search className="h-5 w-5" /> Find User
                  </CardTitle>
                  <CardDescription>Search by email, username, or name</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 mb-4">
                    <Input
                      placeholder="Search users..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearchUsers()}
                      data-testid="user-search-input"
                    />
                    <Button onClick={handleSearchUsers} disabled={searching}>
                      {searching ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {searchResults.length > 0 && (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {searchResults.map(user => (
                        <div 
                          key={user.id}
                          className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                          onClick={() => {
                            setSelectedUser(user);
                            setImpersonateDialogOpen(true);
                          }}
                        >
                          <div>
                            <p className="font-medium">{user.username}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{user.membership_tier}</Badge>
                            {user.is_admin && <Badge className="bg-red-100 text-red-800">Admin</Badge>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="h-5 w-5" /> Quick Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-semibold text-blue-900 mb-2">How Impersonation Works</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• <strong>Read-only:</strong> View what the user sees without making changes</li>
                      <li>• <strong>Full access:</strong> Can perform actions (requires ticket #)</li>
                      <li>• Sessions expire after 1 hour</li>
                      <li>• All actions are logged for audit</li>
                    </ul>
                  </div>
                  
                  {activeSession ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <h4 className="font-semibold text-amber-900 mb-2">Current Session</h4>
                      <p className="text-sm text-amber-800">
                        Viewing as: <strong>{activeSession.target_username}</strong>
                      </p>
                      <p className="text-sm text-amber-800">
                        Mode: {activeSession.mode}
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-2"
                        onClick={handleEndImpersonation}
                      >
                        End Session
                      </Button>
                    </div>
                  ) : (
                    <div className="p-4 bg-gray-50 rounded-lg text-center">
                      <EyeOff className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600">No active impersonation session</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AUDIT HISTORY TAB */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" /> Impersonation History
                </CardTitle>
                <CardDescription>All impersonation sessions and their audit trails</CardDescription>
              </CardHeader>
              <CardContent>
                {sessionHistory.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-600">No impersonation sessions yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Admin</TableHead>
                        <TableHead>Target User</TableHead>
                        <TableHead>Mode</TableHead>
                        <TableHead>Reason</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead>Actions</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sessionHistory.map((session) => (
                        <TableRow key={session.id}>
                          <TableCell className="font-medium">{session.admin_username}</TableCell>
                          <TableCell>{session.target_username}</TableCell>
                          <TableCell>
                            <Badge variant={session.mode === 'full_access' ? 'destructive' : 'secondary'}>
                              {session.mode}
                            </Badge>
                          </TableCell>
                          <TableCell className="max-w-[200px] truncate" title={session.reason}>
                            {session.reason}
                          </TableCell>
                          <TableCell>
                            {session.duration_minutes ? `${session.duration_minutes} min` : 'Active'}
                          </TableCell>
                          <TableCell>{session.actions_count}</TableCell>
                          <TableCell>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewAuditLog(session.id)}
                            >
                              View Log
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* DELETION QUEUE TAB */}
          <TabsContent value="deletions">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Account Deletion Queue</CardTitle>
                    <CardDescription>GDPR Right to Erasure — 30-day grace period auto-purge</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchDeletionQueue} disabled={deletionLoading} data-testid="refresh-deletion-queue">
                      <RefreshCw className={`h-4 w-4 mr-1 ${deletionLoading ? 'animate-spin' : ''}`} /> Refresh
                    </Button>
                    <Button size="sm" variant="destructive" onClick={handleProcessDeletions} disabled={processingDeletion || !deletionQueue.summary?.ready_for_purge} data-testid="process-deletions-btn">
                      <Trash2 className="h-4 w-4 mr-1" /> {processingDeletion ? 'Processing...' : `Purge Now (${deletionQueue.summary?.ready_for_purge || 0})`}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Summary cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">{deletionQueue.summary?.pending || 0}</p>
                    <p className="text-xs text-amber-600">Pending</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">{deletionQueue.summary?.ready_for_purge || 0}</p>
                    <p className="text-xs text-red-600">Ready for Purge</p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-700">{deletionQueue.summary?.completed || 0}</p>
                    <p className="text-xs text-green-600">Completed</p>
                  </div>
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-center">
                    <p className="text-2xl font-bold text-gray-700">{deletionQueue.summary?.cancelled || 0}</p>
                    <p className="text-xs text-gray-600">Cancelled</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mb-4">Auto-purge runs daily at 5:00 AM UTC. Accounts older than 30 days are permanently deleted.</p>

                {deletionQueue.requests?.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No deletion requests</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Requested</TableHead>
                        <TableHead>Days Left</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {deletionQueue.requests?.map((req, idx) => (
                        <TableRow key={idx} data-testid={`deletion-row-${idx}`}>
                          <TableCell className="font-medium">{req.email}</TableCell>
                          <TableCell>
                            <Badge variant={
                              req.status === 'pending' ? (req.grace_expired ? 'destructive' : 'outline') :
                              req.status === 'completed' ? 'default' : 'secondary'
                            }>
                              {req.status === 'pending' && req.grace_expired ? 'EXPIRED' : req.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm">
                            {req.requested_at ? new Date(req.requested_at).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            {req.status === 'pending' ? (
                              <span className={req.grace_expired ? 'text-red-600 font-bold' : 'text-amber-600'}>
                                {req.grace_expired ? 'Overdue' : `${req.days_remaining}d`}
                              </span>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Impersonation Dialog */}
        <Dialog open={impersonateDialogOpen} onOpenChange={setImpersonateDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Start Impersonation Session</DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="font-medium">{selectedUser.username}</p>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
                
                <div>
                  <Label>Mode</Label>
                  <Select
                    value={impersonateForm.mode}
                    onValueChange={(val) => setImpersonateForm({...impersonateForm, mode: val})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="read_only">Read Only - View what user sees</SelectItem>
                      <SelectItem value="full_access">Full Access - Can perform actions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Reason *</Label>
                  <Textarea
                    placeholder="Why are you impersonating this user?"
                    value={impersonateForm.reason}
                    onChange={(e) => setImpersonateForm({...impersonateForm, reason: e.target.value})}
                    required
                    data-testid="impersonate-reason"
                  />
                </div>
                
                {impersonateForm.mode === 'full_access' && (
                  <div>
                    <Label>Ticket Number *</Label>
                    <Input
                      placeholder="TICKET-12345"
                      value={impersonateForm.ticket_number}
                      onChange={(e) => setImpersonateForm({...impersonateForm, ticket_number: e.target.value})}
                      required
                      data-testid="impersonate-ticket"
                    />
                    <p className="text-xs text-amber-600 mt-1">Required for full access mode</p>
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setImpersonateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleStartImpersonation} disabled={startingSession}>
                {startingSession ? 'Starting...' : 'Start Session'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit Log Dialog */}
        <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Session Audit Log</DialogTitle>
            </DialogHeader>
            
            {auditLogs.length === 0 ? (
              <p className="text-center py-4 text-gray-500">No actions logged for this session</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Endpoint</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditLogs.map((log) => (
                      <TableRow key={log.id || `${log.timestamp}-${log.action_type}-${log.endpoint}`}>
                        <TableCell className="text-xs">
                          {new Date(log.timestamp).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{log.action_type}</Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">
                          {log.method} {log.endpoint}
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">
                          {log.details ? JSON.stringify(log.details) : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
